import type { Indicator, MissionStatus, Priority, RiskLevel, Flow, Role } from "./enums.js";
import { can } from "./permissions.js";

/**
 * Modelo del tablero de Misiones: presentación sobre la máquina de estados, sin transiciones propias.
 * Orden de las fases = orden real de `TRANSITIONS`: developing → awaiting_ci → in_review → merging → done.
 */

export const ACTIVE_COLUMNS = ["pending", "planning", "development", "verification", "review"] as const;
export type ActiveColumn = (typeof ACTIVE_COLUMNS)[number];
export const BOARD_COLUMNS = [...ACTIVE_COLUMNS, "closed"] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export const BOARD_COLUMN_LABEL: Record<BoardColumn, string> = {
  pending: "Pendiente", planning: "Planificación", development: "Desarrollo", verification: "Verificación", review: "Revisión", closed: "Cerradas",
};

/**
 * Columnas de la vista «Tablero»: las fases de trabajo más «Completadas recientes». Esta última no es una fase (ninguna misión
 * «está» en ella): es una consulta sobre las completadas de los últimos `RECENT_COMPLETED_DAYS` días, sin cambiar su estado.
 */
export const RECENT_COLUMN = "recent" as const;
export const TABLERO_COLUMNS = [...ACTIVE_COLUMNS, RECENT_COLUMN] as const;
export type TableroColumn = (typeof TABLERO_COLUMNS)[number];
export const TABLERO_COLUMN_LABEL: Record<TableroColumn, string> = { ...BOARD_COLUMN_LABEL, recent: "Completadas recientes" };

/** Qué estados primarios pertenecen a cada fase activa. `merging` (entrega aprobada, esperando merge) cuelga de Revisión: viene de `in_review`. */
export const COLUMN_PRIMARY_STATUSES: Record<ActiveColumn, readonly MissionStatus[]> = {
  pending: ["pending"],
  planning: ["planning", "awaiting_plan_approval"],
  development: ["developing"],
  verification: ["awaiting_ci"],
  review: ["in_review", "merging"],
};

/** Estados que pausan una misión sin sacarla de su fase. */
export const PAUSED_STATUSES = ["blocked", "error", "awaiting_budget"] as const satisfies readonly MissionStatus[];
export const CLOSED_STATUSES = ["done", "cancelled"] as const satisfies readonly MissionStatus[];
const isPaused = (s: MissionStatus) => (PAUSED_STATUSES as readonly string[]).includes(s);
const isClosed = (s: MissionStatus) => (CLOSED_STATUSES as readonly string[]).includes(s);

/** Fase en la que se ve una misión. Las pausadas conservan la fase de la que vienen; las terminadas van a Finalizadas. */
export function boardColumnOf(status: MissionStatus, resumeStatus?: MissionStatus | null): BoardColumn {
  if (isClosed(status)) return "closed";
  if (isPaused(status)) return resumeStatus && !isPaused(resumeStatus) && !isClosed(resumeStatus) ? boardColumnOf(resumeStatus) : "pending";
  for (const c of ACTIVE_COLUMNS) if (COLUMN_PRIMARY_STATUSES[c].includes(status)) return c;
  return "pending";
}

/* ───────────── resultado de las misiones finalizadas ───────────── */

export const CLOSED_RESULTS = ["completed", "closed_unmerged", "rejected", "cancelled"] as const;
export type ClosedResult = (typeof CLOSED_RESULTS)[number];
export const CLOSED_RESULT_LABEL: Record<ClosedResult, string> = {
  completed: "Completada", closed_unmerged: "PR cerrada sin merge", rejected: "Rechazada", cancelled: "Cancelada",
};

/** `outcome` lo fija `transitionMission`: merged/auto_merged/closed_unmerged al terminar; rejected/cancelled al cancelar. */
export function closedResultOf(status: MissionStatus, outcome: string | null | undefined): ClosedResult | null {
  if (status === "done") return outcome === "closed_unmerged" ? "closed_unmerged" : "completed";
  if (status === "cancelled") return outcome === "rejected" ? "rejected" : "cancelled";
  return null;
}

/** Columnas de la vista «Cerradas», una por resultado y en este orden. «PR cerrada sin fusionar» nunca se mezcla con las completadas. */
export const CLOSED_COLUMNS = ["completed", "cancelled", "rejected", "closed_unmerged"] as const satisfies readonly ClosedResult[];
export const CLOSED_COLUMN_LABEL: Record<ClosedResult, string> = {
  completed: "Completadas", cancelled: "Canceladas", rejected: "Rechazadas", closed_unmerged: "PR cerrada sin fusionar",
};

/* ───────────── completadas recientes ───────────── */

/**
 * Una misión completada pertenece a «Completadas recientes» (vista Tablero) durante los 7 días siguientes a su finalización y
 * pasa a «Cerradas › Completadas» después. Es una regla de consulta: no cambia estados ni lanza ningún archivado.
 *
 * Corte único, medido en tiempo transcurrido (7 × 24 h, sin husos ni días naturales) contra un `now` fijado por petición:
 *   · reciente  ⇔  now − fechaDeCierre ≤ 7 días   (con exactamente 7 días todavía es reciente)
 *   · Cerradas  ⇔  now − fechaDeCierre  > 7 días, o la fecha de cierre es desconocida
 * Solo cuenta el resultado «completed»: canceladas, rechazadas y «PR cerrada sin fusionar» están siempre en Cerradas.
 */
export const RECENT_COMPLETED_DAYS = 7;
export const RECENT_COMPLETED_MS = RECENT_COMPLETED_DAYS * 86_400_000;
/** Instante a partir del cual (incluido) una completada sigue siendo reciente. */
export const recentCutoff = (now: Date): Date => new Date(now.getTime() - RECENT_COMPLETED_MS);

/**
 * Fecha efectiva de finalización: `completed_at` (la fija `transitionMission` al pasar a done/cancelled). Si falta (datos
 * anteriores a ese registro o sembrados), la del último `mission.status_changed` hacia el estado actual. Si tampoco hay historial,
 * null: no se inventa una fecha con `updated_at` (que cambia con cualquier edición posterior).
 */
export function closedAtOf(completedAt: Date | null | undefined, changes: readonly StatusChange[], status: MissionStatus): Date | null {
  if (!isClosed(status)) return null;
  if (completedAt) return completedAt;
  for (let i = changes.length - 1; i >= 0; i--) if (changes[i]!.to === status) return changes[i]!.at;
  return null;
}

/** ¿Va a «Completadas recientes»? Espejo en JS de `recentCompleted` (SQL en el servicio); un test contrasta ambos. */
export function isRecentCompletion(result: ClosedResult | null, closedAt: Date | null, now: Date): boolean {
  return result === "completed" && closedAt !== null && closedAt.getTime() >= recentCutoff(now).getTime();
}

/* ───────────── intervención humana requerida ───────────── */

export const WAITING_CAPACITY = "waiting_capacity";
/** Códigos de bloqueo del control de alcance (Fase 1). El primero se resuelve con `decideScope`, nunca con «reanudar». */
export const SCOPE_APPROVAL_REQUIRED = "scope_approval_required";
export const SCOPE_UNVERIFIABLE = "scope_unverifiable";

export type InterventionKind = "approve_plan" | "define_scope" | "approve_delivery" | "start" | "resume" | "extend_budget" | "review_ci" | "decide_scope" | "reduce_scope";
export interface Intervention {
  kind: InterventionKind;
  /** Qué hay que hacer, en una frase. */
  label: string;
  /** ¿Puede hacerlo el rol que consulta? Se calcula con la misma matriz de permisos que valida el backend. */
  canAct: boolean;
}

export interface InterventionInput {
  status: MissionStatus;
  resumeStatus: MissionStatus | null;
  blockCode: string | null;
  startRequested: boolean;
  /**
   * Solo importa en `awaiting_plan_approval`: ¿el plan vigente trae ficheros previstos? Sin ellos no se puede aprobar
   * (`approvePlan` es false) y hay que definir el alcance. `undefined` = no consultado (se trata como con ficheros).
   */
  planHasFiles?: boolean;
}

/**
 * `resume` del detalle: agent.control y el bloqueo no es la espera de capacidad (se reanuda sola) ni una autorización
 * de alcance pendiente (esa se resuelve con `decideScope`).
 */
export const canResumeMission = (role: Role, blockCode: string | null) => can(role, "agent.control") && blockCode !== WAITING_CAPACITY && blockCode !== SCOPE_APPROVAL_REQUIRED;

/**
 * Intervención que necesita una misión. Replica las condiciones de `actions` en `missionDetail`
 * (aprobar plan/entrega, reanudar, ampliar presupuesto, iniciar, corregir CI); un test las contrasta.
 * null = no hay nadie que deba actuar (avanza sola o está cerrada).
 */
export function interventionOf(m: InterventionInput, role: Role): Intervention | null {
  const resume = canResumeMission(role, m.blockCode);
  switch (m.status) {
    case "awaiting_plan_approval":
      // Con o sin ficheros previstos, quien tiene approval.plan puede actuar (aprobar, definir alcance, pedir cambios o rechazar).
      return m.planHasFiles === false
        ? { kind: "define_scope", label: "Definir el alcance del plan: no trae ficheros previstos", canAct: can(role, "approval.plan") }
        : { kind: "approve_plan", label: "Aprobar o rechazar el plan", canAct: can(role, "approval.plan") };
    case "in_review":
      return { kind: "approve_delivery", label: "Aprobar o rechazar la entrega", canAct: can(role, "approval.delivery") };
    case "pending":
      return m.startRequested ? null : { kind: "start", label: "Iniciar la planificación", canAct: can(role, "mission.create") };
    case "awaiting_budget":
      return { kind: "extend_budget", label: "Ampliar el presupuesto o reanudar", canAct: can(role, "approval.budget") || resume };
    case "error":
      return { kind: "resume", label: "Revisar el error y reanudar", canAct: resume };
    case "blocked": {
      // `requestCorrections` del detalle también está activo en un bloqueo que viene de awaiting_ci.
      const corrections = m.resumeStatus === "awaiting_ci" && can(role, "approval.delivery");
      if (m.blockCode === SCOPE_APPROVAL_REQUIRED) return { kind: "decide_scope", label: "Autorizar o devolver los ficheros fuera del plan", canAct: can(role, "approval.plan") || corrections };
      if (m.blockCode === SCOPE_UNVERIFIABLE) return { kind: "reduce_scope", label: "Reducir la PR (diff truncado) y reanudar, o cancelar la misión", canAct: resume || corrections };
      if (m.resumeStatus === "awaiting_ci") return { kind: "review_ci", label: "Revisar el fallo de CI: pedir correcciones o reanudar", canAct: resume || corrections };
      if (m.blockCode === WAITING_CAPACITY) return null;
      return { kind: "resume", label: "Resolver el bloqueo y reanudar", canAct: resume };
    }
    default:
      return null;
  }
}

/* ───────────── tiempos ───────────── */

export interface StatusChange {
  at: Date;
  from: MissionStatus | null;
  to: MissionStatus;
}

/**
 * Desde cuándo está la misión en su fase actual, reconstruido de `mission.status_changed` (orden cronológico).
 * Una pausa no cambia de fase. null si el historial no explica la fase actual (p. ej. datos sembrados sin eventos):
 * mejor no mostrar nada que mostrar un tiempo inventado.
 */
export function phaseSince(createdAt: Date, changes: readonly StatusChange[], status: MissionStatus, resumeStatus: MissionStatus | null): Date | null {
  let col: BoardColumn = "pending";
  let since = createdAt;
  for (const c of changes) {
    const next: BoardColumn = isClosed(c.to) ? "closed" : isPaused(c.to) ? (c.from && !isPaused(c.from) && !isClosed(c.from) ? boardColumnOf(c.from) : col) : boardColumnOf(c.to);
    if (next !== col) {
      col = next;
      since = c.at;
    }
  }
  return col === boardColumnOf(status, resumeStatus) ? since : null;
}

/** Desde cuándo está en el estado actual (p. ej. «bloqueada desde…»). null si no hay dato fiable. */
export function stateSince(createdAt: Date, changes: readonly StatusChange[], status: MissionStatus): Date | null {
  for (let i = changes.length - 1; i >= 0; i--) if (changes[i]!.to === status) return changes[i]!.at;
  return status === "pending" && changes.length === 0 ? createdAt : null;
}

export const REASON_MAX = 140;
/** Motivo breve para la tarjeta; el texto completo se lee en el detalle. */
export function briefReason(text: string | null | undefined, max = REASON_MAX): string | null {
  const t = (text ?? "").replace(/\s+/g, " ").trim();
  if (!t) return null;
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/* ───────────── contratos de la API del tablero ───────────── */

/**
 * board: en curso + completadas recientes · attention: en curso donde puede actuar el rol (nunca completadas) ·
 * closed: terminadas fuera de la ventana de recientes (y todas las canceladas, rechazadas y PR cerradas sin fusionar).
 */
export const BOARD_SCOPES = ["board", "attention", "closed"] as const;
export type BoardScope = (typeof BOARD_SCOPES)[number];

export interface BoardCardDTO {
  id: string;
  key: string;
  title: string;
  status: MissionStatus;
  column: BoardColumn;
  indicator: Indicator | null;
  /** Motivo breve de una pausa (bloqueada/error/sin presupuesto) o del cierre (cancelada/rechazada). */
  reason: string | null;
  intervention: Intervention | null;
  priority: Priority;
  flow: Flow | null;
  riskLevel: RiskLevel;
  projectId: string;
  projectName: string;
  companyId: string;
  companyName: string;
  repositoryId: string;
  repositoryName: string;
  agentRunning: boolean;
  prNumber: number | null;
  prUrl: string | null;
  /** Tres tiempos distintos: creación, última actividad registrada y entrada en la fase actual (null si no es fiable). */
  createdAt: string;
  lastActivityAt: string;
  phaseSince: string | null;
  stateSince: string | null;
  /** Fecha efectiva de finalización (ver `closedAtOf`). null en una misión abierta, o cerrada sin historial que la fije. */
  closedAt: string | null;
  result: ClosedResult | null;
  /** Completada dentro de la ventana de recientes: la que se muestra en «Completadas recientes». */
  recentlyCompleted: boolean;
}

export interface BoardPageDTO {
  items: BoardCardDTO[];
  /** Total que cumple los filtros, no solo lo cargado. */
  total: number;
  nextCursor: string | null;
}

export interface BoardCountsDTO {
  columns: Record<ActiveColumn, { total: number; running: number; attention: number }>;
  /** Misiones en curso (las cinco fases de trabajo). No incluye las completadas recientes: tienen su propio contador. */
  active: number;
  /** Completadas dentro de la ventana de recientes (columna «Completadas recientes» del Tablero). */
  recent: number;
  /** Misiones que requieren acción del usuario que consulta (según su rol). Nunca incluye completadas. */
  attention: number;
  /** Vista «Cerradas»: sin las completadas recientes, que están en el Tablero. `total = Σ byResult`. */
  closed: { total: number; byResult: Record<ClosedResult, number> };
}
